import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import HeroSlider from "../components/HeroSlider";
import CityGuidesSection from "../components/CityGuidesSection";
import SegmentsSection from "../components/SegmentsSection";
import CategoryRail from "../components/CategoryRail";
import { PlanPill, OuroBadge, CashbackBadge } from "../components/Badges";
import { MapPin, Coins, Sparkles, ArrowRight, Star, ShoppingBag } from "lucide-react";

const DEFAULT_ORDER = ["slides", "segments", "cities", "shop", "how_it_works", "guides", "ctas", "highlights"];

export default function Landing() {
  const [cities, setCities] = useState([]);
  const [slides, setSlides] = useState([]);
  const [highlights, setHighlights] = useState([]);
  const [shopPicks, setShopPicks] = useState([]);
  const [order, setOrder] = useState(DEFAULT_ORDER);

  useEffect(() => {
    api.get("/cities").then((r) => setCities(r.data));
    api.get("/slides").then((r) => setSlides(r.data || []));
    api.get("/highlights").then((r) => setHighlights(r.data || [])).catch(() => {});
    api.get("/products/shop?page=1&page_size=6").then((r) => setShopPicks(r.data?.items || [])).catch(() => {});
    api.get("/settings/public").then((r) => {
      const o = r.data?.home_sections_order;
      if (Array.isArray(o) && o.length) {
        // Append any new section keys that weren't present in the saved order
        const merged = [...o, ...DEFAULT_ORDER.filter((k) => !o.includes(k))];
        setOrder(merged);
      }
    }).catch(() => {});
  }, []);

  const renderSection = (key) => {
    switch (key) {
      case "slides":
        return (
          <React.Fragment key="slides">
            <CategoryRail />
            <HeroSlider key="slides-hero" slides={slides} />
          </React.Fragment>
        );
      case "segments":
        return <SegmentsSection key="segments" />;
      case "cities":
        return (
          <section key="cities" className="max-w-7xl mx-auto px-6 py-16">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-8">
              <div>
                <span className="text-xs uppercase tracking-[0.2em] text-stone-500 font-bold">Escolha sua cidade</span>
                <h2 className="font-display text-3xl sm:text-4xl font-bold text-stone-900 mt-2">Descobrir o comércio local</h2>
              </div>
              <Link to="/discover/all" className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-900 hover:text-blue-950" data-testid="view-all-cities">
                Ver todas no mapa <ArrowRight size={14}/>
              </Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {cities.map((c) => (
                <Link key={c.slug} to={`/discover/${c.slug}`} className="group relative overflow-hidden rounded-3xl bg-white border border-stone-200/60 hover:-translate-y-1 hover:shadow-2xl transition-all duration-300 min-h-[220px] flex flex-col justify-end" data-testid={`discover-city-${c.slug}`}>
                  {c.cover_image && <img src={c.cover_image} alt={c.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/>}
                  <div className="absolute inset-0 bg-gradient-to-t from-blue-950/85 via-blue-950/40 to-transparent"/>
                  <div className="relative p-6 text-white">
                    <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-white/80">{c.state} · Brasil</span>
                    <h3 className="font-display text-2xl font-bold mt-1">{c.name}</h3>
                    <div className="flex items-center justify-between mt-4">
                      <span className="text-xs text-white/85 flex items-center gap-1"><MapPin size={12}/> {c.lat.toFixed(3)}, {c.lng.toFixed(3)}</span>
                      <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-white text-blue-950 group-hover:bg-amber-400 transition-colors"><ArrowRight size={16}/></span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        );
      case "how_it_works":
        return (
          <section key="how" className="max-w-7xl mx-auto px-6 py-16 border-t border-stone-200">
            <div className="grid lg:grid-cols-3 gap-8">
              {[
                { icon: MapPin, title: "Encontre", desc: "Veja empresas locais no mapa, filtre por categoria, cashback e mais." },
                { icon: Coins, title: "Compre", desc: "Ganhe cashback dinâmico (definido por cada empresa) em parceiros Prata ou Ouro." },
                { icon: Sparkles, title: "Use sua moeda", desc: "Acumule créditos na sua carteira global e use em qualquer cidade parceira." },
              ].map((s, i) => (
                <div key={i} className="p-6 rounded-2xl bg-white border border-stone-200/60 shadow-sm">
                  <div className="w-12 h-12 rounded-2xl bg-blue-50 text-blue-900 flex items-center justify-center mb-4"><s.icon size={22}/></div>
                  <h3 className="font-display text-lg font-semibold text-stone-900">{s.title}</h3>
                  <p className="text-stone-600 text-sm mt-1 leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
          </section>
        );
      case "guides":
        return <CityGuidesSection key="guides" />;
      case "shop":
        if (shopPicks.length === 0) return null;
        return (
          <section key="shop" className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
            <div className="flex items-end justify-between flex-wrap gap-3 mb-5 sm:mb-8">
              <div>
                <span className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-amber-700 font-bold flex items-center gap-1.5"><ShoppingBag size={12}/> Shop MorarBem</span>
                <h2 className="font-display text-2xl sm:text-4xl font-bold text-stone-900 mt-2">Compre direto pelo app</h2>
                <p className="text-stone-500 text-xs sm:text-sm mt-1">Produtos e serviços de comerciantes locais com cashback no PIX.</p>
              </div>
              <Link to="/shop" className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-bold text-blue-900 hover:text-blue-950 px-3 py-2 rounded-xl bg-amber-100 hover:bg-amber-200 border border-amber-300 transition-colors" data-testid="home-shop-link">
                Ver tudo no Shop <ArrowRight size={14}/>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3">
              {shopPicks.slice(0, 6).map((p) => (
                <Link key={p.id} to={`/product/${p.id}`} className="group rounded-xl sm:rounded-2xl bg-white border border-stone-200 overflow-hidden hover:-translate-y-1 hover:shadow-xl transition-all" data-testid={`home-shop-${p.id}`}>
                  <div className="aspect-square bg-stone-100 overflow-hidden relative">
                    {p.image_url ? (
                      <img src={p.image_url} alt={p.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-stone-300"><ShoppingBag size={28}/></div>
                    )}
                    {p.cashback_pct_on_checkout > 0 && (
                      <span className="absolute bottom-1.5 right-1.5 inline-flex items-center px-1.5 py-0.5 rounded-md bg-emerald-100/95 text-emerald-900 text-[9px] font-bold backdrop-blur-sm">+{p.cashback_pct_on_checkout}%</span>
                    )}
                  </div>
                  <div className="p-2">
                    <h3 className="font-display font-semibold text-stone-900 leading-tight text-xs line-clamp-2 min-h-[2rem]">{p.title}</h3>
                    {p.price > 0 && <div className="font-display text-sm font-bold text-blue-950 mt-1">R$ {p.price.toFixed(2).replace(".", ",")}</div>}
                  </div>
                </Link>
              ))}
            </div>
          </section>
        );
      case "ctas":
        return (
          <section key="ctas" className="max-w-7xl mx-auto px-6 py-16 grid sm:grid-cols-2 gap-5">
            <Link to="/para-empresas" className="group relative overflow-hidden rounded-3xl bg-gradient-to-br from-blue-950 to-blue-900 p-8 text-white border-2 border-amber-400/40 hover:border-amber-400 transition-all hover:-translate-y-1 hover:shadow-2xl" data-testid="cta-business">
              <span className="text-xs uppercase tracking-[0.25em] text-amber-400 font-bold">Para empresas</span>
              <h3 className="font-display text-2xl sm:text-3xl font-bold mt-2">Mais clientes, mais retorno</h3>
              <p className="text-sm text-blue-100 mt-2 max-w-md">Cashback é o jeito mais barato de fidelizar. Comece grátis e cresça quando quiser.</p>
              <span className="inline-flex items-center gap-1 mt-4 text-amber-400 font-bold">Saiba mais <ArrowRight size={16}/></span>
            </Link>
            <Link to="/para-clientes" className="group relative overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-600 to-emerald-800 p-8 text-white border-2 border-emerald-400/40 hover:border-emerald-300 transition-all hover:-translate-y-1 hover:shadow-2xl" data-testid="cta-customer">
              <span className="text-xs uppercase tracking-[0.25em] text-emerald-200 font-bold">Para clientes</span>
              <h3 className="font-display text-2xl sm:text-3xl font-bold mt-2">Compre perto e ganhe dinheiro de volta</h3>
              <p className="text-sm text-emerald-100 mt-2 max-w-md">Carteira global, sem taxas, sem complicação. Acumule cashback em cada compra.</p>
              <span className="inline-flex items-center gap-1 mt-4 text-emerald-200 font-bold">Saiba mais <ArrowRight size={16}/></span>
            </Link>
          </section>
        );
      case "highlights":
        if (highlights.length === 0) return null;
        return (
          <section key="highlights" className="max-w-7xl mx-auto px-6 pb-16 border-t border-stone-200 pt-16">
            <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
              <div>
                <span className="text-xs uppercase tracking-[0.2em] text-amber-700 font-bold flex items-center gap-1.5"><Star size={12}/> Destaques</span>
                <h2 className="font-display text-3xl sm:text-4xl font-bold text-stone-900 mt-2">Selecionados para você</h2>
              </div>
              <Link to="/discover/all" className="text-sm font-semibold text-blue-900 hover:text-blue-950" data-testid="see-all-link">Ver todos →</Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {highlights.map((h, idx) => {
                const c = h.company; if (!c) return null;
                const isOuro = c.plan === "OURO";
                const showCashback = c.accepts_cashback && (c.plan === "PRATA" || c.plan === "OURO");
                const cover = c.cover_url || c.media?.[0]?.url;
                return (
                  <Link key={h.id} to={`/company/${c.id}`} className={`group relative rounded-3xl bg-white border ${isOuro ? "border-amber-400 ouro-glow" : "border-stone-200"} hover:-translate-y-1 hover:shadow-2xl transition-all duration-300 min-h-[280px] flex flex-col`} data-testid={`highlight-${idx}`}>
                    <div className="aspect-[16/10] bg-stone-100 relative overflow-hidden rounded-t-3xl">
                      {cover ? <img src={cover} alt={c.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/> :
                        <div className="absolute inset-0 flex items-center justify-center text-stone-300 font-display text-6xl">{c.name[0]}</div>}
                      <div className="absolute top-3 left-3 inline-flex items-center gap-1 bg-amber-400 text-amber-950 px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider shadow"><Star size={11}/> #{idx + 1}</div>
                    </div>
                    {c.logo_url && (
                      <div className="absolute top-[calc(62.5%-1.75rem)] left-4 w-14 h-14 rounded-2xl border-4 border-white shadow-lg overflow-hidden bg-white flex items-center justify-center z-10">
                        <img src={c.logo_url} alt="logo" className="w-full h-full object-contain p-1"/>
                      </div>
                    )}
                    <div className="p-4 pt-9 flex-1 flex flex-col">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-display font-semibold text-stone-900 text-lg">{c.name}</h3>
                        <PlanPill plan={c.plan}/>
                      </div>
                      <p className="text-xs text-stone-500 mt-0.5">{c.category} · {c.city_slug}</p>
                      <p className="text-sm text-stone-600 line-clamp-2 mt-2">{c.description}</p>
                      <div className="mt-auto pt-3 flex flex-wrap gap-1.5">
                        {isOuro && <OuroBadge/>}
                        {showCashback && <CashbackBadge/>}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)]">
      {order.map((key) => renderSection(key))}
    </div>
  );
}
